import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { fromEvent as observableFromEvent, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { asViewRef } from '@recruitee/utils';

import { ComponentPortal, TemplatePortal } from '../../core/portal/portal.module';
import { DirDirective, LayoutDirection } from '../rtl/rtl.module';
import { OverlayRef } from './overlay-ref';
import { OverlayState } from './overlay-state';
import { OverlayService } from './overlay.service';
import {
  ConnectedOverlayPositionChange,
  ConnectionPositionPair,
} from './position/connected-position';
import { ConnectedPositionStrategy } from './position/connected-position-strategy';
import { ScrollDispatcher } from './scroll/scroll-dispatcher';

// First word described where the dropdown will land,
// Second word describes its align
// F.e. "BottomLeft" means menu will appear in the bottom and will be aligned to the left
// F.e. "RightTop" means menu will appear in the right and will be aligned to the top
export enum OverlayPosition {
  BottomLeft = 'bottomleft',
  BottomRight = 'bottomright',
  TopLeft = 'topleft',
  TopRight = 'topright',
  RightBottom = 'rightbottom',
  RightTop = 'righttop',
  LeftBottom = 'leftbottom',
  LeftTop = 'lefttop',
}

/**
 * Directive applied to an element to make it usable as an origin for an Overlay using a
 * ConnectedPositionStrategy.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-overlay-origin]',
  exportAs: 'rtOverlayOrigin',
})
export class OverlayOriginDirective {
  constructor(public elementRef: ElementRef) {}
}

/** Default set of positions for the overlay. Follows the behavior of a dropdown. */
export const positionPairs = {
  [OverlayPosition.BottomLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'top' },
  ),
  [OverlayPosition.BottomRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.TopLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.TopRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftBottom]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftTop]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.RightBottom]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.RightTop]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'start', overlayY: 'top' },
  ),
};

const defaultPositions = {
  [OverlayPosition.BottomLeft]: [
    positionPairs[OverlayPosition.BottomLeft],
    positionPairs[OverlayPosition.TopLeft],
  ],
  [OverlayPosition.BottomRight]: [
    positionPairs[OverlayPosition.BottomRight],
    positionPairs[OverlayPosition.TopRight],
  ],
  [OverlayPosition.TopLeft]: [
    positionPairs[OverlayPosition.TopLeft],
    positionPairs[OverlayPosition.BottomLeft],
  ],
  [OverlayPosition.TopRight]: [
    positionPairs[OverlayPosition.TopRight],
    positionPairs[OverlayPosition.BottomRight],
  ],
  [OverlayPosition.RightBottom]: [
    positionPairs[OverlayPosition.RightBottom],
    positionPairs[OverlayPosition.RightTop],
  ],
  [OverlayPosition.RightTop]: [
    positionPairs[OverlayPosition.RightTop],
    positionPairs[OverlayPosition.RightBottom],
  ],
  [OverlayPosition.LeftBottom]: [
    positionPairs[OverlayPosition.LeftBottom],
    positionPairs[OverlayPosition.LeftTop],
  ],
  [OverlayPosition.LeftTop]: [
    positionPairs[OverlayPosition.LeftTop],
    positionPairs[OverlayPosition.LeftBottom],
  ],
};

/**
 * Directive to facilitate declarative creation of an Overlay using a ConnectedPositionStrategy.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-connected-overlay]',
  exportAs: 'rtConnectedOverlay',
})
export class ConnectedOverlayDirective implements OnDestroy {
  // Registered connected position pairs.
  @Input()
  public set positions(v: ConnectionPositionPair[]) {
    this._positions = v;
  }

  public get positions(): ConnectionPositionPair[] {
    return this._positions && this._positions.length
      ? this._positions
      : Array.isArray(this.position)
      ? this.position.reduce(
          (agg, positionKey: OverlayPosition) => [...agg, ...defaultPositions[positionKey]],
          [],
        )
      : defaultPositions[this.position];
  }

  /** The offset in pixels for the overlay connection point on the x-axis */
  @Input()
  public get offsetX(): number {
    return this._offsetX;
  }

  public set offsetX(offsetX: number) {
    this._offsetX = offsetX;
    if (this._position) {
      this._position.withOffsetX(offsetX);
    }
  }

  /** The offset in pixels for the overlay connection point on the y-axis */
  @Input()
  public get offsetY(): number {
    return this._offsetY;
  }

  public set offsetY(offsetY: number) {
    this._offsetY = offsetY;
    if (this._position) {
      this._position.withOffsetY(offsetY);
    }
  }

  /** Whether or not the overlay should attach a backdrop. */
  public static ngAcceptInputType_hasBackdrop: boolean | '';
  @Input()
  public get hasBackdrop(): boolean {
    return this._hasBackdrop;
  }

  // TODO: move the boolean coercion logic to a shared function in core
  public set hasBackdrop(value: boolean) {
    this._hasBackdrop = value != null && `${value}` !== 'false';
  }

  /** Wheter or not the overlay should stay in place on scroll **/
  @Input()
  public get isPinned(): boolean {
    return this._isPinned;
  }

  public set isPinned(value: boolean) {
    this._isPinned = value != null && `${value}` !== 'false';
  }

  @Input()
  public get open(): boolean {
    return this._open;
  }

  public set open(value: boolean) {
    Promise.resolve().then(() => {
      if (this._open !== value) {
        this.toggleOverlay(value);
      }
    });
  }

  public get positionStrategy(): ConnectedPositionStrategy {
    return this._position;
  }

  public set portal(portal: TemplatePortal | ComponentPortal<any>) {
    this._templatePortal = portal;
  }

  /** The associated overlay reference. */
  public get overlayRef(): OverlayRef {
    return this._overlayRef;
  }

  /** The element's layout direction. */
  public get dir(): LayoutDirection {
    return this._dir ? this._dir.value : 'ltr';
  }

  /** Origin for the connected overlay. */
  @Input() public origin: OverlayOriginDirective;

  @Input('rtConnectedOverlayPosition')
  public position: string[] | OverlayPosition = [
    OverlayPosition.BottomRight,
    OverlayPosition.BottomLeft,
    OverlayPosition.TopRight,
    OverlayPosition.TopLeft,
  ];

  @Input('rtConnectedOverlayAutoClose') public autoCloseOnUnpreventedClick: boolean = true;

  /** The width of the overlay panel. */
  @Input() public width: number | string;

  /** The height of the overlay panel. */
  @Input() public height: number | string;

  /** The min width of the overlay panel. */
  @Input() public minWidth: number | string;

  /** The min height of the overlay panel. */
  @Input() public minHeight: number | string;

  /** The custom class to be set on the backdrop element. */
  @Input() public backdropClass: string;

  /** Event emitted when the backdrop is clicked. */
  @Output() public backdropClick: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the component is clicked. */
  @Output() public componentAutoCloseClick: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the position has changed. */
  @Output()
  public positionChange: EventEmitter<ConnectedOverlayPositionChange> = new EventEmitter<ConnectedOverlayPositionChange>();

  /** Event emitted when the overlay has been attached. */
  @Output() public attach: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the overlay has been detached. */
  @Output() public detach: EventEmitter<void> = new EventEmitter<void>();
  private _overlayRef: OverlayRef;
  private _templatePortal: TemplatePortal | ComponentPortal<any>;
  private _open: boolean = false;
  private _hasBackdrop: boolean = false;
  private _isPinned: boolean = true;
  private _backdropSubscription: Subscription | null;
  private _positionSubscription: Subscription;
  private _offsetX: number = 0;
  private _offsetY: number = 0;
  private _position: ConnectedPositionStrategy;

  private _positions: ConnectionPositionPair[];

  private scrollSubscription: Subscription;

  // TODO (jzielinski) animations

  constructor(
    @Inject(TemplateRef)
    public templateRef: TemplateRef<any> | null,
    public viewContainerRef: ViewContainerRef,
    private _overlay: OverlayService,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    @Host() private parentCdRef: ChangeDetectorRef,
    @Optional() private scrollDispatcher: ScrollDispatcher,
    @Optional() private _dir: DirDirective,
    @Optional() @Inject('overlayLevel') private _level: number,
  ) {
    this._templatePortal = new TemplatePortal(templateRef, viewContainerRef);
  }

  public ngOnDestroy(): void {
    this._destroyOverlay();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { width } = changes;

    if (width && this._overlayRef) {
      const newState = { ...this._overlayRef.getState(), width: this.width };
      this._overlayRef.updateState(newState);
      this.updatePositionWithoutRedrawing();
    }
  }

  public openAt(origin: OverlayOriginDirective): void {
    this.origin = origin;
    this.toggleOverlay(true);
  }

  public toggleOverlay(value: false): void;
  public toggleOverlay(value: true): Map<string, any> | ComponentRef<any>;
  public toggleOverlay(value: boolean): Map<string, any> | ComponentRef<any> | void;
  public toggleOverlay(value: boolean): Map<string, any> | ComponentRef<any> | void {
    this._open = value;
    const result = value ? this._attachOverlay() : this._detachOverlay();
    if (!asViewRef(this.parentCdRef).destroyed) {
      this.parentCdRef.detectChanges();
    }
    return result;
  }

  public recreateOverlay(): void {
    this._destroyOverlay();
    this._createOverlay();
  }

  public updatePosition(): void {
    if (this.open) {
      // TODO instead of recreating the overlay, just update its' position in-place
      this.open = false;
      this.open = true;
    }
  }

  public updatePositionWithoutRedrawing(): void {
    if (this.open) {
      if (this._overlayRef) {
        this._overlayRef.updateSize();
        this._overlayRef.updatePosition();
      }
    }
  }

  /** Creates an overlay */
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create(this._buildConfig());
  }

  /** Builds the overlay config based on the directive's inputs */
  private _buildConfig(): OverlayState {
    const overlayConfig = new OverlayState();

    if (this.width || this.width === 0) {
      overlayConfig.width = this.width;
    }

    if (this.height || this.height === 0) {
      overlayConfig.height = this.height;
    }

    if (this.minWidth || this.minWidth === 0) {
      overlayConfig.minWidth = this.minWidth;
    }

    if (this.minHeight || this.minHeight === 0) {
      overlayConfig.minHeight = this.minHeight;
    }

    overlayConfig.hasBackdrop = this.hasBackdrop;

    if (this.backdropClass) {
      overlayConfig.backdropClass = this.backdropClass;
    } else {
      overlayConfig.backdropClass = 'blank-backdrop';
    }

    if (!this.origin) {
      throw new Error(
        `.origin prop seems to be missing in component with ConnectedOverlayDirective: ${this}`,
      );
    }

    this._position = <ConnectedPositionStrategy>this._createPositionStrategy();
    overlayConfig.positionStrategy = this._position;

    overlayConfig.direction = this.dir;

    overlayConfig.level = this._level || 0;

    return overlayConfig;
  }

  /** Returns the position strategy of the overlay to be set on the overlay config */
  private _createPositionStrategy(): ConnectedPositionStrategy {
    const pos = this.positions[0];
    const originPoint = { originX: pos.originX, originY: pos.originY };
    const overlayPoint = { overlayX: pos.overlayX, overlayY: pos.overlayY };
    const strategy = this._overlay
      .position()
      .connectedTo(this.origin.elementRef, originPoint, overlayPoint)
      .withOffsetX(this.offsetX)
      .withOffsetY(this.offsetY);

    this._handlePositionChanges(strategy);

    return strategy;
  }

  private _handlePositionChanges(strategy: ConnectedPositionStrategy): void {
    for (let i = 0; i < this.positions.length; i++) {
      strategy.withFallbackPosition(
        { originX: this.positions[i].originX, originY: this.positions[i].originY },
        { overlayX: this.positions[i].overlayX, overlayY: this.positions[i].overlayY },
      );
    }

    this._positionSubscription = strategy.onPositionChange.subscribe(pos =>
      this.positionChange.emit(pos),
    );
  }

  /** Attaches the overlay and subscribes to backdrop clicks if backdrop exists
   Also subscribes to scroll if in pinned mode,
   */
  private _attachOverlay(): Map<string, any> | ComponentRef<any> {
    if (!this._overlayRef) {
      this._createOverlay();
    }

    this.attach.emit();

    if (this.hasBackdrop) {
      this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
        this.backdropClick.emit();
      });
    }

    if (this.scrollDispatcher && this.isPinned) {
      this.scrollSubscription = this.ngZone.runOutsideAngular(() => {
        let tick = false;

        return this.scrollDispatcher.scrolled(0, () => {
          if (this._overlayRef) {
            if (!tick) {
              tick = true;
              requestAnimationFrame(() => {
                this._overlayRef.updatePosition();
                tick = false;
              });
            }
          }
        });
      });
    }

    if (this.autoCloseOnUnpreventedClick) {
      observableFromEvent(this._overlayRef.overlayElement, 'click')
        .pipe(takeUntil(this.detach.asObservable()))
        .subscribe((event: MouseEvent) => {
          if (!event.defaultPrevented) {
            this.open = false;
            this.componentAutoCloseClick.emit();
          }
          // @ts-ignore
          if (!this.cdRef['destroyed']) {
            this.cdRef.detectChanges();
          }
          return event;
        });
    }

    const result = this._overlayRef.attach(
      <ComponentPortal<any> | TemplatePortal>this._templatePortal,
    );

    this.ngZone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        this._overlayRef.updatePosition();
        // @ts-ignore
        if (!this.cdRef['destroyed']) {
          this.cdRef.detectChanges();
        }
      }),
    );

    return result;
  }

  /** Detaches the overlay and unsubscribes to backdrop clicks if backdrop exists */
  private _detachOverlay(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }

    if (this._overlayRef) {
      this._overlayRef.detach();
      this.detach.emit();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
      this._backdropSubscription = null;
    }
  }

  /** Destroys the overlay created by this directive. */
  private _destroyOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
    }
    if (this._positionSubscription) {
      this._positionSubscription.unsubscribe();
    }
  }
}
